import * as React from "react";
import pluginRegistry, { ActionEditProps} from "../pluginRegistry";
import { BaseComponent } from "components/BaseComponent/BaseComponent";
import SpecialVariables from "client/specialVariables";
import Roles from "../Roles";
import { ActionSummaryProps } from "../actionSummaryProps";
import { ActionExecutionLocation } from "client/resources";
import { ExpandableFormSection, Summary, FormSectionHeading } from "components/form";
import PackageSelector, { PackageSelectorProperties } from "components/PackageSelector/PackageSelector";
import { StringRadioButtonGroup } from "components/form/RadioButton/RadioButtonGroup";
import RadioButton from "components/form/RadioButton/RadioButton";
import Note from "components/form/Note/Note";
import { VariableLookupCodeEditor } from "components/CodeEditor/CodeEditor";
import ExternalLink from "components/Navigation/ExternalLink/ExternalLink";
import FeedResource, {BuiltInFeedId} from "client/resources/feedResource";
import { repository } from "clientInstance";
import CommonSummaryHelper from "utils/CommonSummaryHelper/CommonSummaryHelper";
import { VariableLookupText } from "components/form/Text/Text";
import {
    default as PackageDownloadOptions,
    PackageDownloadOptionsProperties
} from "components/PackageDownloadOptions/PackageDownloadOptions";
import { ScriptingLanguage } from "components/scriptingLanguage";
import ScriptingLanguageSelector from "components/ScriptingLanguageSelector/ScriptingLanguageSelector";
import { CardFill } from "components/form/Sections/ExpandableFormSection";
import { TargetRoles } from "areas/projects/components/DeploymentProcess/ActionDetails";
import { PackageRequirement } from "client/resources";

class ScriptActionSummary extends BaseComponent<ActionSummaryProps, any> {
    render() {
        const runsOnServer = this.props.properties[SpecialVariables.Action.RunOnServer] === "true";

        return runsOnServer ?
            <div>Run a script on the Octopus Server {this.props.targetRolesAsCSV &&
                <span>on behalf of targets in <Roles rolesAsCSV={this.props.targetRolesAsCSV} /></span>}</div> :
            <div>Run a script across targets in <Roles rolesAsCSV={this.props.targetRolesAsCSV} /></div>;
    }
}

export interface ScriptProperties extends PackageDownloadOptionsProperties, PackageSelectorProperties {
    "Octopus.Action.Script.ScriptSource": string;
    "Octopus.Action.Script.Syntax": string;
    "Octopus.Action.Script.ScriptBody": string;
    "Octopus.Action.Script.ScriptFileName": string;
    "Octopus.Action.Script.ScriptParameters": string;
    "Octopus.Action.RunOnServer": string;
}

interface ScriptActionEditState {
    feeds: FeedResource[];
    originalCanRunBeforeAcquisition: boolean;
    originalStepPackageRequirement: PackageRequirement;
}

interface ScriptActionEditProps extends ActionEditProps<ScriptProperties> {
    powerShellOnly?: boolean;
}

// Note: this is also used in the Azure PowerShell step and the ServiceFabric PowerShell step
export class ScriptActionEdit extends BaseComponent<ScriptActionEditProps, ScriptActionEditState> {
    private static defaultProps: Partial<ScriptActionEditProps> = {
        powerShellOnly: false
    };

    constructor(props: any) {
        super(props);

        const packageAcquisition = props.additionalActions && props.additionalActions.packageAcquisition;

        this.state = {
            feeds: [],
            originalCanRunBeforeAcquisition: packageAcquisition ? packageAcquisition.canRunBeforeAcquisition : false,
            originalStepPackageRequirement: packageAcquisition ? packageAcquisition.stepPackageRequirement : PackageRequirement.LetOctopusDecide
        };
    }

    componentDidMount() {
        this.props.doBusyTask(async () => {
            if (!this.props.properties["Octopus.Action.Script.ScriptSource"]) {
                this.handleScriptSourceChanged("Inline", true);
            }
            const feeds = await repository.Feeds.all();
            this.setState({ feeds });
        });
    }

    handleScriptSourceChanged(val: string, initialise: boolean = false) {
        const isRunFromPackage = val === "Package";
        const properties: any = { ["Octopus.Action.Script.ScriptSource"]: val };

        if (isRunFromPackage) {
            properties["Octopus.Action.Script.Syntax"] = null;
            properties["Octopus.Action.Script.ScriptBody"] = null;
            properties["Octopus.Action.Package.FeedId"] = BuiltInFeedId;
        } else {
            if (!this.props.properties["Octopus.Action.Script.Syntax"] && !this.props.powerShellOnly) {
                properties["Octopus.Action.Script.Syntax"] = "PowerShell";
            }
            properties["Octopus.Action.Script.ScriptFileName"] = null;
            properties["Octopus.Action.Package.FeedId"] = null;
            properties["Octopus.Action.Package.PackageId"] = null;
        }

        this.props.setProperties(properties, initialise);

        // if we are pulling the script from a package we might need to override things
        // if the user has set this step to run before package acquisition
        if (this.props.additionalActions) {
            if (isRunFromPackage) {
                this.props.additionalActions.packageAcquisition.onCanRunBeforeAcquisitionChanged(false);
            } else {
                this.props.additionalActions.packageAcquisition.onCanRunBeforeAcquisitionChanged(this.state.originalCanRunBeforeAcquisition);
            }
            if (this.state.originalStepPackageRequirement === PackageRequirement.BeforePackageAcquisition) {
                const newValue = isRunFromPackage
                    ? PackageRequirement.LetOctopusDecide
                    : this.state.originalStepPackageRequirement;

                this.props.additionalActions.packageAcquisition.onStepPackageRequirementChanged(newValue);
            }
        }
    }

    scriptSourceSummary() {
        if (this.props.properties["Octopus.Action.Script.ScriptSource"] === "Inline") {
            return Summary.default("The script is defined below");
        } else {
            return Summary.summary("The script is contained in a package");
        }
    }

    scriptBodySummary() {
        if (!this.props.properties["Octopus.Action.Script.ScriptBody"]) {
            return Summary.placeholder("The script body has not been defined");
        }
        if (this.props.powerShellOnly) {
            return Summary.summary("The script body has been defined");
        }
        const syntax = this.props.properties["Octopus.Action.Script.Syntax"];
        if (syntax === "FSharp") {
            return Summary.summary(<span>An <strong>F#</strong> script has been defined</span>);
        } else if (syntax === "CSharp") {
            return Summary.summary(<span>A <strong>C#</strong> script has been defined</span>);
        }
        return Summary.summary(<span>A <strong>{syntax}</strong> script has been defined</span>);
    }

    packageScriptSummary() {
        const fileName = this.props.properties["Octopus.Action.Script.ScriptFileName"];
        if (!fileName) {
            return Summary.placeholder("The script file has not been defined");
        }
        return Summary.summary(<span>The script <strong>{fileName}</strong> from the selected package will be run</span>);
    }

    render() {
        const properties = this.props.properties;
        const runOnServer = this.props.properties
            && this.props.properties["Octopus.Action.RunOnServer"]
            && this.props.properties["Octopus.Action.RunOnServer"].toLowerCase() === "true";

        return <div>
            <FormSectionHeading title="Script" />
            <ExpandableFormSection
                errorKey="scriptsource"
                title="Script Source"
                summary={this.scriptSourceSummary()}
                help="Select where the script is defined.">
                <StringRadioButtonGroup
                    value={properties["Octopus.Action.Script.ScriptSource"]}
                    onChange={val => this.handleScriptSourceChanged(val)}>
                    <RadioButton value="Inline" label="Source code" />
                    <Note>The script body is defined in the Script content field below</Note>
                    <RadioButton value="Package" label="Script file inside a package" />
                    <Note>The script is contained in a package that will be deployed</Note>
                </StringRadioButtonGroup>
            </ExpandableFormSection>

            {properties["Octopus.Action.Script.ScriptSource"] === "Inline" && <ExpandableFormSection
                errorKey="Octopus.Action.Script.ScriptBody"
                title="Script Content"
                fillCardWidth={CardFill.FillRight}
                summary={this.scriptBodySummary()}
                help={this.props.powerShellOnly
                    ? "Enter the body of the script that will be executed."
                    : "Select the script language and enter the body of the script that will be executed."}>
                {!this.props.powerShellOnly &&
                    <ScriptingLanguageSelector value={properties["Octopus.Action.Script.Syntax"] as ScriptingLanguage}
                        onlyRunnableOnServer={runOnServer}
                        onChange={syntax => this.props.setProperties({ ["Octopus.Action.Script.Syntax"]: syntax })} />
                }
                {this.props.powerShellOnly && <h4>PowerShell script</h4>}
                <VariableLookupCodeEditor
                    localNames={this.props.localNames}
                    projectId={this.props.projectId}
                    syntax={this.props.powerShellOnly ? ScriptingLanguage.PowerShell : properties["Octopus.Action.Script.Syntax"] as ScriptingLanguage}
                    value={properties["Octopus.Action.Script.ScriptBody"]}
                    language={this.props.powerShellOnly ? ScriptingLanguage.PowerShell : properties["Octopus.Action.Script.Syntax"] as ScriptingLanguage}
                    allowFullScreen={true}
                    onChange={(x) => this.props.setProperties({ ["Octopus.Action.Script.ScriptBody"]: x })} />
            </ExpandableFormSection>}

            {properties["Octopus.Action.Script.ScriptSource"] === "Package" && <div>
                <ExpandableFormSection
                    errorKey="Octopus.Action.Package.FeedId|Octopus.Action.Package.PackageId"
                    title="Package"
                    summary={CommonSummaryHelper.packageSummary(properties, this.state.feeds)}
                    help={<span>This step is used to run a script. The package that you wish to deploy should contain
                        your script and any files it needs to access. <ExternalLink href="DocumentationPackaging">
                            Learn more about what your packages should contain, and how to create them</ExternalLink>.</span>}>
                    <PackageSelector
                        setProperties={this.props.setProperties}
                        properties={this.props.properties as any}
                        projectId={this.props.projectId}
                        feeds={this.state.feeds}
                        getFieldError={this.props.getFieldError}
                        localNames={this.props.localNames}
                        errors={this.props.errors} />
                    <PackageDownloadOptions
                        setProperties={this.props.setProperties}
                        properties={this.props.properties}
                        projectId={this.props.projectId}
                        localNames={this.props.localNames}
                        errors={this.props.errors} />
                </ExpandableFormSection>
                <ExpandableFormSection
                    errorKey="Octopus.Action.Script.ScriptFileName|Octopus.Action.Script.ScriptParameters"
                    title="Script File"
                    summary={this.packageScriptSummary()}
                    help={"Enter the relative path to the script file within your package and any parameters that need to be passed to the script."}>
                    <VariableLookupText
                        localNames={this.props.localNames}
                        projectId={this.props.projectId}
                        value={properties["Octopus.Action.Script.ScriptFileName"]}
                        onChange={(x) => this.props.setProperties({ ["Octopus.Action.Script.ScriptFileName"]: x })}
                        error={this.props.getFieldError("Octopus.Action.Script.ScriptFileName")}
                        label="Script file name" />
                    <Note>
                        The relative path to the script file within the package.<br />
                        e.g. <code>MyScript.ps1</code> or <code>Scripts\MyScript.ps1</code>
                    </Note>
                    <VariableLookupText
                        localNames={this.props.localNames}
                        projectId={this.props.projectId}
                        value={properties["Octopus.Action.Script.ScriptParameters"]}
                        onChange={(x) => this.props.setProperties({ ["Octopus.Action.Script.ScriptParameters"]: x })}
                        error={this.props.getFieldError("Octopus.Action.Script.ScriptParameters")}
                        label="Script parameters" />
                    {this.props.powerShellOnly
                        ? <Note>
                            Parameters expected by the script. Use platform specific calling convention.<br />
                            e.g. <code>-Path #{`{VariableStoringPath}`}</code> for PowerShell
                        </Note>
                        : <Note>
                            Parameters expected by the script. Use platform specific calling convention.<br />
                            e.g. <code>-Path #{`{VariableStoringPath}`}</code> for PowerShell or <code>-- #{`{VariableStoringPath}`}</code> for ScriptCS
                        </Note>
                    }

                </ExpandableFormSection>
            </div>}
        </div>;
    }
}

pluginRegistry.registerDeploymentAction({
    actionType: "Octopus.Script",
    summary: (properties, targetRolesAsCSV) => <ScriptActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV} />,
    edit: ScriptActionEdit,
    canHaveChildren: step => !!step.Properties[SpecialVariables.Action.TargetRoles],
    canBeChild: true,
    executionLocation: ActionExecutionLocation.TargetOrServer,
    targetRoleOption: TargetRoles.Optional,
    hasPackages: (action) => {
        if (action.Properties && action.Properties["Octopus.Action.Script.ScriptSource"] === "Package") {
            return true;
        }
        return false;
    }
});


// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./app/components/Actions/script/scriptAction.tsx